import clsx from "clsx";
import type { ReactNode } from "react";
import Icon, { type IconName } from "../components/Icon";

export type Tone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

const soft: Record<Tone, string> = {
  neutral: "border-neutral-line bg-neutral-soft text-neutral-ink",
  accent: "border-accent-line bg-accent-soft text-accent",
  success: "border-success-line bg-success-soft text-success-ink",
  warning: "border-warning-line bg-warning-soft text-warning-ink",
  danger: "border-danger-line bg-danger-soft text-danger-ink",
  info: "border-info-line bg-info-soft text-info-ink",
};

const solid: Record<Tone, string> = {
  neutral: "border-transparent bg-neutral-ink text-surface",
  accent: "border-transparent bg-accent text-accent-fg",
  success: "border-transparent bg-success text-success-fg",
  warning: "border-transparent bg-warning text-white",
  danger: "border-transparent bg-danger text-white",
  info: "border-transparent bg-info text-white",
};

const dotColour: Record<Tone, string> = {
  neutral: "bg-faint",
  accent: "bg-accent",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
};

export default function Badge({
  children,
  tone = "neutral",
  variant = "soft",
  icon,
  dot,
  className,
  title,
}: {
  children: ReactNode;
  tone?: Tone;
  variant?: "soft" | "solid";
  icon?: IconName;
  /** Точка вместо иконки — для статусов, где важен только цвет. */
  dot?: boolean;
  className?: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={clsx(
        "inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-0.5",
        "text-2xs font-medium leading-4",
        variant === "solid" ? solid[tone] : soft[tone],
        className,
      )}
    >
      {dot && (
        <span
          aria-hidden="true"
          className={clsx("h-1.5 w-1.5 shrink-0 rounded-full", dotColour[tone])}
        />
      )}
      {icon && <Icon name={icon} size={12} />}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Точка-индикатор без подписи (легенда рядом или в title). */
export function StatusDot({
  tone = "neutral",
  pulse,
  className,
  title,
}: {
  tone?: Tone;
  pulse?: boolean;
  className?: string;
  title?: string;
}) {
  return (
    <span title={title} className={clsx("relative inline-flex h-2 w-2", className)}>
      {pulse && (
        <span
          className={clsx(
            "absolute inset-0 animate-ping rounded-full opacity-60",
            dotColour[tone],
          )}
        />
      )}
      <span className={clsx("relative h-2 w-2 rounded-full", dotColour[tone])} />
    </span>
  );
}

/** Счётчик на пункте меню или вкладке. */
export function Counter({
  value,
  tone = "neutral",
  className,
}: {
  value: number;
  tone?: Tone;
  className?: string;
}) {
  if (!value) return null;
  return (
    <span
      className={clsx(
        "inline-flex h-4 min-w-4 items-center justify-center rounded px-1",
        "text-2xs font-semibold tabular-nums leading-none",
        tone === "danger"
          ? "bg-danger text-white"
          : tone === "accent"
            ? "bg-accent text-accent-fg"
            : "bg-neutral-line text-neutral-ink",
        className,
      )}
    >
      {value > 99 ? "99+" : value}
    </span>
  );
}
